import type { OperatorPermission, PlatformPermission } from "@strust/contracts";
import { useMe } from "../features/shell/api/me.ts";

export interface Permissions {
  readonly isLoading: boolean;
  /** True when the user holds the code on that operator (or on any operator when omitted). */
  readonly can: (permission: OperatorPermission, operatorId?: string) => boolean;
  /** The operators on which the user holds the code. */
  readonly operatorsWith: (permission: OperatorPermission) => readonly string[];
  /** True when the user holds the platform-level code (Platform Administrator only). */
  readonly canPlatform: (permission: PlatformPermission) => boolean;
  /** True when the user holds any platform permission: the "Platform" area is shown only then. */
  readonly hasPlatformAccess: boolean;
}

/**
 * Permission gates mirror the backend check (the API stays the authority): the codes come from
 * `GET /me`, resolved per operator.
 */
export function usePermissions(): Permissions {
  const me = useMe();
  const operators = me.data?.operators ?? [];
  const platform = me.data?.platform_permissions ?? [];
  const operatorsWith = (permission: OperatorPermission) =>
    operators.filter((o) => o.permissions.includes(permission)).map((o) => o.operator_id);
  return {
    isLoading: me.isPending,
    can: (permission, operatorId) =>
      operatorId === undefined
        ? operatorsWith(permission).length > 0
        : operatorsWith(permission).includes(operatorId),
    operatorsWith,
    canPlatform: (permission) => platform.includes(permission),
    hasPlatformAccess: platform.length > 0,
  };
}
